import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { ADMIN } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import BookingDocuments from '@/components/BookingDocuments';
import BulkActionsBar, { RowCheckbox } from '@/components/BulkActionsBar';
import { REPORT_ACCEPT_ATTR, MAX_FILE_SIZE_LABEL, ALLOWED_REPORT_TYPES, MAX_FILE_SIZE_BYTES } from '@/lib/fileValidation';
import { Shield, Users, Calendar, CheckSquare, FileText, ChevronRight, ChevronDown, Upload, Download, MessageSquare, Star, Check, X, Trash2, RotateCcw, Key, Edit3, UserCog, UserPlus, ArrowUpDown, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_ORDER = [
  'intake_submitted',
  'deposit_paid',
  'report_issued',
  'next_stage_confirmed',
  'next_stage_booked',
  'next_stage_paid',
  'completed'
];

const STAGE_LABELS = {
  intake_submitted: 'Intake Submitted',
  deposit_paid: 'Deposit Paid',
  report_issued: 'Report Issued',
  next_stage_confirmed: 'Next Stage Confirmed',
  next_stage_booked: 'Next Stage Booked',
  next_stage_paid: 'Next Stage Paid',
  completed: 'Completed'
};

function formatSlot(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  });
}

export default function Admin() {
  const { token, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [reportDrafts, setReportDrafts] = useState({});
  const [stageDrafts, setStageDrafts] = useState({});
  const [serviceDrafts, setServiceDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(null);
  const [pdfError, setPdfError] = useState({});
  const [activeTab, setActiveTab] = useState('bookings');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewStatusFilter, setReviewStatusFilter] = useState('pending');
  const [moderatingId, setModeratingId] = useState(null);
  const [bookingFilter, setBookingFilter] = useState('active'); // active | deleted | all
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteBooking, setConfirmDeleteBooking] = useState(null);
  // === User management state ===
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState('client');
  const [userSearch, setUserSearch] = useState('');
  const [resetUserTarget, setResetUserTarget] = useState(null); // user being password-reset
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState('');
  const [editUserTarget, setEditUserTarget] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', phone: '', organization: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  // === Add-user state ===
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    name: '', email: '', password: '', role: 'client', phone: '', organization: ''
  });
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  // === Delete-user state ===
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deleteUserSaving, setDeleteUserSaving] = useState(false);
  // === Bulk select state per tab ===
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [selectedReviews, setSelectedReviews] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(null); // { kind, action, ids }
  // === Sort state per tab ===
  const [bookingSort, setBookingSort] = useState('created_desc');
  const [reviewSort, setReviewSort] = useState('created_desc');
  const [userSort, setUserSort] = useState('created_desc');
  // === Booking stage filter ===
  const [bookingStageFilter, setBookingStageFilter] = useState('all');
  // === Review stars filter ===
  const [reviewStarsFilter, setReviewStarsFilter] = useState('all');

  const isAdmin = user?.role === 'admin';

  const fetchAll = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    try {
      const params =
        bookingFilter === 'deleted'
          ? '?only_deleted=true'
          : bookingFilter === 'all'
            ? '?include_deleted=true'
            : '';
      const [bookingsRes, statsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/bookings${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services-catalog`)
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setServices(servicesRes.data);
    } catch (err) {
      console.error('Admin fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, bookingFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchReviews = useCallback(async (status = reviewStatusFilter) => {
    if (!isAdmin) return;
    setReviewsLoading(true);
    try {
      const url = status === 'all' ? `${API}/admin/reviews` : `${API}/admin/reviews?status=${encodeURIComponent(status)}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(res.data);
    } catch (err) {
      console.error('Reviews fetch failed:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, [token, isAdmin, reviewStatusFilter]);

  useEffect(() => {
    if (activeTab === 'reviews') fetchReviews(reviewStatusFilter);
  }, [activeTab, reviewStatusFilter, fetchReviews]);

  const handleModerateReview = async (reviewId, newStatus) => {
    setModeratingId(reviewId);
    try {
      await axios.put(
        `${API}/admin/reviews/${reviewId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReviews(reviewStatusFilter);
    } catch (err) {
      console.error('Review moderation failed:', err);
    } finally {
      setModeratingId(null);
    }
  };

  const handleStageUpdate = async (bookingId) => {
    const stage = stageDrafts[bookingId];
    const reportText = reportDrafts[bookingId];
    const serviceId = serviceDrafts[bookingId];
    if (!stage) return;
    setSavingId(bookingId);
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}/stage`,
        {
          stage,
          report_text: reportText || null,
          next_stage_service_id: serviceId || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAll();
    } catch (err) {
      console.error('Stage update failed:', err);
    } finally {
      setSavingId(null);
    }
  };

  const handleReportPdfUpload = async (bookingId, file) => {
    if (!file) return;
    setPdfError({ ...pdfError, [bookingId]: '' });

    if (!ALLOWED_REPORT_TYPES.includes(file.type)) {
      setPdfError({ ...pdfError, [bookingId]: 'Only PDF files allowed for reports' });
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setPdfError({ ...pdfError, [bookingId]: `File too large. Max ${MAX_FILE_SIZE_LABEL}` });
      return;
    }

    setUploadingPdf(bookingId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/admin/bookings/${bookingId}/report-pdf`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      fetchAll();
    } catch (err) {
      setPdfError({ ...pdfError, [bookingId]: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploadingPdf(null);
    }
  };

  const handleAccessUpdate = async (bookingId, { locked, expiresInDays }) => {
    try {
      const params = new URLSearchParams();
      params.append('locked', String(locked));
      if (expiresInDays !== null && expiresInDays !== undefined && expiresInDays !== '') {
        params.append('expires_in_days', String(expiresInDays));
      }
      await axios.put(
        `${API}/admin/bookings/${bookingId}/access?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAll();
    } catch (err) {
      console.error('Access update failed:', err);
    }
  };

  const handleDeleteBooking = async () => {
    if (!confirmDeleteBooking) return;
    setDeletingId(confirmDeleteBooking.id);
    try {
      await axios.delete(`${API}/bookings/${confirmDeleteBooking.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfirmDeleteBooking(null);
      fetchAll();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestoreBooking = async (bookingId) => {
    setRestoringId(bookingId);
    try {
      await axios.post(
        `${API}/admin/bookings/${bookingId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAll();
    } catch (err) {
      console.error('Restore failed:', err);
    } finally {
      setRestoringId(null);
    }
  };

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (userRoleFilter && userRoleFilter !== 'all') params.set('role', userRoleFilter);
      if (userSearch.trim()) params.set('q', userSearch.trim());
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data || []);
    } catch (err) {
      console.error('Users fetch failed:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [token, isAdmin, userRoleFilter, userSearch]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, fetchUsers]);

  const handleResetUserPassword = async () => {
    if (!resetUserTarget) return;
    setResetError('');
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      setResetError('Password must be at least 6 characters.');
      return;
    }
    setResetSaving(true);
    try {
      await axios.post(
        `${API}/admin/users/${resetUserTarget.id}/reset-password`,
        { new_password: resetPasswordValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetUserTarget(null);
      setResetPasswordValue('');
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Could not reset password.');
    } finally {
      setResetSaving(false);
    }
  };

  const openEditUser = (u) => {
    setEditUserTarget(u);
    setEditUserForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      organization: u.organization || ''
    });
    setEditError('');
  };

  const handleSaveUser = async () => {
    if (!editUserTarget) return;
    setEditError('');
    setEditSaving(true);
    try {
      await axios.put(
        `${API}/admin/users/${editUserTarget.id}`,
        editUserForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditUserTarget(null);
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Could not save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  // === Add User ===
  const handleAddUser = async () => {
    setAddUserError('');
    const f = addUserForm;
    if (!f.name || !f.email || !f.password) {
      setAddUserError('Name, email and password are required.');
      return;
    }
    if (f.password.length < 6) {
      setAddUserError('Password must be at least 6 characters.');
      return;
    }
    setAddUserSaving(true);
    try {
      await axios.post(
        `${API}/admin/users`,
        f,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddUserOpen(false);
      setAddUserForm({ name: '', email: '', password: '', role: 'client', phone: '', organization: '' });
      fetchUsers();
    } catch (err) {
      setAddUserError(err.response?.data?.detail || 'Could not create user.');
    } finally {
      setAddUserSaving(false);
    }
  };

  // === Delete a single User ===
  const handleDeleteUser = async () => {
    if (!confirmDeleteUser) return;
    setDeleteUserSaving(true);
    try {
      await axios.delete(`${API}/admin/users/${confirmDeleteUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfirmDeleteUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Delete user failed:', err);
    } finally {
      setDeleteUserSaving(false);
    }
  };

  // === Bulk-action utilities ===
  const runBulk = async (jobs) => {
    setBulkBusy(true);
    try {
      await Promise.allSettled(jobs);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkBookings = async (action) => {
    const ids = Array.from(selectedBookings);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (action === 'delete') {
        await axios.post(`${API}/admin/bookings/bulk-delete`, { ids },
          { headers: { Authorization: `Bearer ${token}` } });
      } else if (action === 'restore') {
        // Restore is per-row (no bulk endpoint) — loop in parallel
        await Promise.all(ids.map((id) =>
          axios.post(`${API}/admin/bookings/${id}/restore`, {},
            { headers: { Authorization: `Bearer ${token}` } })
        ));
      }
    } finally {
      setBulkBusy(false);
    }
    setSelectedBookings(new Set());
    setConfirmBulk(null);
    fetchAll();
  };

  const handleBulkReviews = async (action) => {
    const ids = Array.from(selectedReviews);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (action === 'delete') {
        await axios.post(`${API}/admin/reviews/bulk-delete`, { ids },
          { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const status = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : null;
        if (status) {
          await Promise.all(ids.map((id) =>
            axios.put(`${API}/admin/reviews/${id}`, { status },
              { headers: { Authorization: `Bearer ${token}` } })
          ));
        }
      }
    } finally {
      setBulkBusy(false);
    }
    setSelectedReviews(new Set());
    setConfirmBulk(null);
    fetchReviews(reviewStatusFilter);
  };

  const handleBulkUsers = async (action) => {
    const ids = Array.from(selectedUsers);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (action === 'delete') {
        await axios.post(`${API}/admin/users/bulk-delete`, { ids },
          { headers: { Authorization: `Bearer ${token}` } });
      }
    } finally {
      setBulkBusy(false);
    }
    setSelectedUsers(new Set());
    setConfirmBulk(null);
    fetchUsers();
  };

  // === Sorted / filtered derived data per tab ===
  const sortedBookings = useMemo(() => {
    const filtered = bookingStageFilter === 'all'
      ? bookings
      : bookings.filter((b) => b.stage === bookingStageFilter);
    const arr = [...filtered];
    const dateOf = (b) => (b.created_at ? new Date(b.created_at).getTime() : 0);
    const slotOf = (b) => (b.preferred_slot ? new Date(b.preferred_slot).getTime() : 0);
    if (bookingSort === 'created_desc') arr.sort((a, b) => dateOf(b) - dateOf(a));
    else if (bookingSort === 'created_asc') arr.sort((a, b) => dateOf(a) - dateOf(b));
    else if (bookingSort === 'slot_asc') arr.sort((a, b) => slotOf(a) - slotOf(b));
    else if (bookingSort === 'slot_desc') arr.sort((a, b) => slotOf(b) - slotOf(a));
    else if (bookingSort === 'payment') arr.sort((a, b) => (a.payment_status || '').localeCompare(b.payment_status || ''));
    return arr;
  }, [bookings, bookingSort, bookingStageFilter]);

  const sortedReviews = useMemo(() => {
    const filtered = reviewStarsFilter === 'all'
      ? reviews
      : reviews.filter((r) => Number(r.stars) === Number(reviewStarsFilter));
    const arr = [...filtered];
    const dateOf = (r) => (r.created_at ? new Date(r.created_at).getTime() : 0);
    if (reviewSort === 'created_desc') arr.sort((a, b) => dateOf(b) - dateOf(a));
    else if (reviewSort === 'created_asc') arr.sort((a, b) => dateOf(a) - dateOf(b));
    else if (reviewSort === 'stars_desc') arr.sort((a, b) => (b.stars || 0) - (a.stars || 0));
    else if (reviewSort === 'stars_asc') arr.sort((a, b) => (a.stars || 0) - (b.stars || 0));
    return arr;
  }, [reviews, reviewSort, reviewStarsFilter]);

  const sortedUsers = useMemo(() => {
    const arr = [...users];
    const dateOf = (u) => (u.created_at ? new Date(u.created_at).getTime() : 0);
    if (userSort === 'created_desc') arr.sort((a, b) => dateOf(b) - dateOf(a));
    else if (userSort === 'created_asc') arr.sort((a, b) => dateOf(a) - dateOf(b));
    else if (userSort === 'name_asc') arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (userSort === 'email_asc') arr.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    return arr;
  }, [users, userSort]);

  // === Selection helpers ===
  const toggleSet = (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-20 mt-16 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6 text-slate-300" />
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Admin Access Required</h1>
          <p className="text-slate-600">This area is restricted to All Build Consult administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  const nextStageOptions = STAGE_ORDER;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-7 h-7 text-black" />
            <h1 className="text-4xl font-bold tracking-tight text-black" style={{ fontFamily: 'Outfit' }}>
              Admin Dashboard
            </h1>
          </div>
          <p className="text-slate-600">All Build Consult team controls — advance booking stages, issue reports, manage workflows.</p>
        </div>

        {stats && (
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <div className="grid-border bg-white p-4">
              <Users className="w-5 h-5 text-blue-600 mb-2" />
              <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{stats.total_clients}</div>
              <p className="text-xs text-slate-600">Clients</p>
            </div>
            <div className="grid-border bg-white p-4">
              <Users className="w-5 h-5 text-purple-600 mb-2" />
              <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{stats.total_consultants}</div>
              <p className="text-xs text-slate-600">Consultants</p>
            </div>
            <div className="grid-border bg-white p-4">
              <Calendar className="w-5 h-5 text-blue-600 mb-2" />
              <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{stats.total_bookings}</div>
              <p className="text-xs text-slate-600">Total Bookings</p>
            </div>
            <div className="grid-border bg-white p-4">
              <CheckSquare className="w-5 h-5 text-green-600 mb-2" />
              <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{stats.paid_bookings}</div>
              <p className="text-xs text-slate-600">Paid Bookings</p>
            </div>
            <div className="grid-border bg-white p-4">
              <FileText className="w-5 h-5 text-black mb-2" />
              <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{stats.completed_workflows}</div>
              <p className="text-xs text-slate-600">Completed</p>
            </div>
          </div>
        )}

        {/* Tab toggle */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
          <button
            data-testid={ADMIN.tabBookings}
            onClick={() => setActiveTab('bookings')}
            className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 transition-all ${activeTab === 'bookings' ? 'border-black text-black' : 'border-transparent text-slate-500 hover:text-black'}`}
          >
            <Calendar className="w-4 h-4" />
            Bookings
          </button>
          <button
            data-testid={ADMIN.tabReviews}
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 transition-all ${activeTab === 'reviews' ? 'border-black text-black' : 'border-transparent text-slate-500 hover:text-black'}`}
          >
            <MessageSquare className="w-4 h-4" />
            Client Reviews
          </button>
          <button
            data-testid="admin-tab-users"
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 transition-all ${activeTab === 'users' ? 'border-black text-black' : 'border-transparent text-slate-500 hover:text-black'}`}
          >
            <UserCog className="w-4 h-4" />
            User Management
          </button>
        </div>

        {activeTab === 'users' && (
          <div data-testid="admin-users-panel" className="grid-border bg-white mb-8">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold inline-flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <UserCog className="w-5 h-5" />
                  User Management
                </h2>
                <p className="text-xs text-slate-500">
                  Reset passwords, update profile details for clients &amp; consultants.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  data-testid="admin-users-search"
                  type="text"
                  placeholder="Search name or email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600 w-56"
                />
                <select
                  data-testid="admin-users-role-filter"
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                >
                  <option value="client">Clients</option>
                  <option value="consultant">Consultants</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>

            {usersLoading ? (
              <div className="p-10 text-center text-sm text-slate-500">Loading users…</div>
            ) : users.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">No users match this filter.</div>
            ) : (
              <div>
                <div className="px-4 pt-3">
                  <BulkActionsBar
                    selectedCount={selectedUsers.size}
                    totalCount={users.filter((u) => u.role !== 'admin').length}
                    onClear={() => setSelectedUsers(new Set())}
                    onSelectAll={() => setSelectedUsers(new Set(users.filter((u) => u.role !== 'admin').map((u) => u.id)))}
                    onDelete={() => handleBulkUsers('delete')}
                    entityLabel="user"
                    confirmMessage={`Permanently delete ${selectedUsers.size} user${selectedUsers.size === 1 ? '' : 's'}? Their bookings will be soft-archived but the user accounts will be removed.`}
                    testIdPrefix="admin-users-bulk"
                    disabledReason={bulkBusy ? 'Working…' : undefined}
                  />
                </div>
                {users.length > 1 && users.some((u) => u.role !== 'admin') && (
                  <div className="px-4 pb-2 text-xs text-slate-600">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <RowCheckbox
                        checked={selectedUsers.size > 0 && users.filter((u) => u.role !== 'admin').every((u) => selectedUsers.has(u.id))}
                        onChange={(c) => {
                          if (c) setSelectedUsers(new Set(users.filter((u) => u.role !== 'admin').map((u) => u.id)));
                          else setSelectedUsers(new Set());
                        }}
                        testId="admin-users-select-all-page"
                        label="Select all on this page"
                      />
                      <span>Select all on this page</span>
                    </label>
                  </div>
                )}
                <div className="divide-y divide-slate-200">
                  {users.map((u) => {
                    const isAdmin = u.role === 'admin';
                    const checked = selectedUsers.has(u.id);
                    return (
                  <div key={u.id} data-testid="admin-user-row" data-user-id={u.id} className={`p-4 transition-all flex items-center justify-between gap-3 flex-wrap ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="pt-1 flex-shrink-0">
                        <RowCheckbox
                          checked={checked}
                          disabled={isAdmin}
                          onChange={(c) => {
                            setSelectedUsers((prev) => {
                              const next = new Set(prev);
                              if (c) next.add(u.id); else next.delete(u.id);
                              return next;
                            });
                          }}
                          testId={`admin-user-select-${u.id}`}
                          label={isAdmin ? 'Admin accounts cannot be bulk-deleted' : `Select user ${u.email}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-black truncate">{u.name}</span>
                        <span className={`px-2 py-0.5 text-[10px] uppercase font-semibold tracking-wide rounded-sm ${u.role === 'admin' ? 'bg-purple-100 text-purple-800' : u.role === 'consultant' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-700'}`}>
                          {u.role}
                        </span>
                        {u.referral_code && (
                          <span className="text-[10px] text-slate-500 font-mono">ref: {u.referral_code}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {u.email}
                        {u.phone && <> · {u.phone}</>}
                        {u.organization && <> · {u.organization}</>}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Joined {new Date(u.created_at).toLocaleDateString()}
                      </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        data-testid={`admin-user-edit-${u.id}`}
                        onClick={() => openEditUser(u)}
                        disabled={u.role === 'admin'}
                        title={u.role === 'admin' ? 'Admin profile cannot be edited here' : 'Edit profile'}
                        className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        data-testid={`admin-user-reset-${u.id}`}
                        onClick={() => { setResetUserTarget(u); setResetPasswordValue(''); setResetError(''); }}
                        disabled={u.role === 'admin'}
                        title={u.role === 'admin' ? 'Admin password is managed via env var' : 'Reset this user\'s password'}
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-sm hover:bg-blue-700 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Key className="w-3.5 h-3.5" />
                        Reset Password
                      </button>
                    </div>
                  </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div data-testid={ADMIN.reviewsList} className="grid-border bg-white mb-8">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Client Reviews</h2>
                <p className="text-xs text-slate-500">Approve to publish on the landing page · Reject to hide.</p>
              </div>
              <select
                data-testid={ADMIN.reviewStatusFilter}
                value={reviewStatusFilter}
                onChange={(e) => setReviewStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>

            {reviewsLoading ? (
              <div className="p-10 text-center text-sm text-slate-500">Loading reviews…</div>
            ) : reviews.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500">
                No {reviewStatusFilter === 'all' ? '' : reviewStatusFilter} reviews.
              </div>
            ) : (
              <div>
                <div className="px-4 pt-3">
                  <BulkActionsBar
                    selectedCount={selectedReviews.size}
                    totalCount={reviews.length}
                    onClear={() => setSelectedReviews(new Set())}
                    onSelectAll={() => setSelectedReviews(new Set(reviews.map((r) => r.id)))}
                    onDelete={() => handleBulkReviews('delete')}
                    entityLabel="review"
                    confirmMessage={`Permanently delete ${selectedReviews.size} review${selectedReviews.size === 1 ? '' : 's'}? This cannot be undone.`}
                    testIdPrefix="admin-reviews-bulk"
                    disabledReason={bulkBusy ? 'Working…' : undefined}
                  />
                </div>
                {reviews.length > 1 && (
                  <div className="px-4 pb-2 text-xs text-slate-600">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <RowCheckbox
                        checked={selectedReviews.size > 0 && reviews.every((r) => selectedReviews.has(r.id))}
                        onChange={(c) => {
                          if (c) setSelectedReviews(new Set(reviews.map((r) => r.id)));
                          else setSelectedReviews(new Set());
                        }}
                        testId="admin-reviews-select-all-page"
                        label="Select all on this page"
                      />
                      <span>Select all on this page</span>
                    </label>
                  </div>
                )}
                <div className="divide-y divide-slate-200">
                  {reviews.map((r) => {
                    const checked = selectedReviews.has(r.id);
                    return (
                  <div key={r.id} data-testid={ADMIN.reviewRow} data-review-id={r.id} className={`p-4 transition-all ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="pt-1 flex-shrink-0">
                          <RowCheckbox
                            checked={checked}
                            onChange={(c) => {
                              setSelectedReviews((prev) => {
                                const next = new Set(prev);
                                if (c) next.add(r.id); else next.delete(r.id);
                                return next;
                              });
                            }}
                            testId={`admin-review-select-${r.id}`}
                            label={`Select review by ${r.client_name}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold text-black">{r.client_name}</span>
                          <span className="inline-flex items-center gap-0.5 text-amber-500">
                            {Array.from({ length: r.stars }).map((_, i) => (
                              <Star key={i} className="w-3.5 h-3.5 fill-current" />
                            ))}
                          </span>
                          <span className={`px-2 py-0.5 text-[10px] uppercase font-semibold tracking-wide rounded-sm ${r.status === 'approved' ? 'bg-green-100 text-green-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                            {r.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-black mb-1">{r.title}</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.comment}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          Submitted {new Date(r.created_at).toLocaleString()}
                          {r.moderated_at && ` · Moderated ${new Date(r.moderated_at).toLocaleString()}`}
                        </p>
                        </div>
                      </div>
                      {r.status === 'pending' && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            data-testid={ADMIN.reviewApproveButton}
                            disabled={moderatingId === r.id}
                            onClick={() => handleModerateReview(r.id, 'approved')}
                            className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-sm hover:bg-green-700 transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Approve
                          </button>
                          <button
                            data-testid={ADMIN.reviewRejectButton}
                            disabled={moderatingId === r.id}
                            onClick={() => handleModerateReview(r.id, 'rejected')}
                            className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-sm hover:bg-slate-50 transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" />
                            Reject
                          </button>
                        </div>
                      )}
                      {r.status !== 'pending' && (
                        <button
                          disabled={moderatingId === r.id}
                          onClick={() => handleModerateReview(r.id, r.status === 'approved' ? 'rejected' : 'approved')}
                          className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-sm hover:bg-slate-50 transition-all"
                        >
                          {r.status === 'approved' ? 'Unapprove' : 'Re-approve'}
                        </button>
                      )}
                    </div>
                  </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bookings' && (
        <div data-testid={ADMIN.bookingsTable} className="grid-border bg-white">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
              {bookingFilter === 'deleted' ? 'Deleted Bookings (Audit / Recovery)' : 'All Bookings'}
            </h2>
            <select
              data-testid="admin-booking-filter"
              value={bookingFilter}
              onChange={(e) => setBookingFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
            >
              <option value="active">Active</option>
              <option value="deleted">Deleted</option>
              <option value="all">All (incl. deleted)</option>
            </select>
          </div>

          {bookings.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <p>
                {bookingFilter === 'deleted'
                  ? 'No deleted bookings.'
                  : 'No bookings yet'}
              </p>
            </div>
          ) : (
            <div>
              <div className="px-4 pt-3">
                <BulkActionsBar
                  selectedCount={selectedBookings.size}
                  totalCount={bookings.filter((b) => !b.deleted_at).length}
                  onClear={() => setSelectedBookings(new Set())}
                  onSelectAll={() => setSelectedBookings(new Set(bookings.filter((b) => !b.deleted_at).map((b) => b.id)))}
                  onDelete={() => handleBulkBookings('delete')}
                  entityLabel="booking"
                  confirmMessage={`Soft-delete ${selectedBookings.size} booking${selectedBookings.size === 1 ? '' : 's'}? They will move to the Deleted audit view and can be restored from there.`}
                  testIdPrefix="admin-bookings-bulk"
                  disabledReason={bulkBusy ? 'Working…' : undefined}
                />
              </div>
              {bookings.length > 1 && bookings.some((b) => !b.deleted_at) && (
                <div className="px-4 pb-2 text-xs text-slate-600">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <RowCheckbox
                      checked={selectedBookings.size > 0 && bookings.filter((b) => !b.deleted_at).every((b) => selectedBookings.has(b.id))}
                      onChange={(c) => {
                        if (c) setSelectedBookings(new Set(bookings.filter((b) => !b.deleted_at).map((b) => b.id)));
                        else setSelectedBookings(new Set());
                      }}
                      testId="admin-bookings-select-all-page"
                      label="Select all active bookings on this page"
                    />
                    <span>Select all on this page</span>
                  </label>
                </div>
              )}
            <div className="divide-y divide-slate-200">
              {bookings.map((b) => {
                const isExpanded = expandedId === b.id;
                const isDeleted = !!b.deleted_at;
                const isSelected = selectedBookings.has(b.id);
                return (
                  <div key={b.id} data-testid={ADMIN.bookingRow} data-booking-id={b.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setExpandedId(isExpanded ? null : b.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedId(isExpanded ? null : b.id);
                        }
                      }}
                      className={`w-full p-4 transition-all text-left flex items-center justify-between gap-4 cursor-pointer ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : isDeleted ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`}
                    >
                      <div
                        className="flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <RowCheckbox
                          checked={isSelected}
                          disabled={isDeleted}
                          onChange={(c) => {
                            setSelectedBookings((prev) => {
                              const next = new Set(prev);
                              if (c) next.add(b.id); else next.delete(b.id);
                              return next;
                            });
                          }}
                          testId={`admin-booking-select-${b.id}`}
                          label={isDeleted ? 'Already deleted — restore individually' : `Select booking ${b.service_type}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`font-semibold ${isDeleted ? 'text-slate-500 line-through' : 'text-black'}`}>
                            {b.service_type}
                          </span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-sm">
                            {STAGE_LABELS[b.stage] || b.stage}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-sm ${b.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                            {b.payment_status}
                          </span>
                          {isDeleted && (
                            <span
                              data-testid="admin-deleted-badge"
                              className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm"
                            >
                              Deleted by {b.deleted_by_role || 'unknown'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {b.user_id} · {formatSlot(b.preferred_slot || b.preferred_date)} · A${b.budget.toFixed(2)}
                          {isDeleted && b.deleted_at && (
                            <span> · removed {new Date(b.deleted_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isDeleted ? (
                          <button
                            data-testid={`admin-restore-${b.id}`}
                            onClick={(e) => { e.stopPropagation(); handleRestoreBooking(b.id); }}
                            disabled={restoringId === b.id}
                            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-sm hover:bg-blue-700 transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            {restoringId === b.id ? 'Restoring…' : 'Restore'}
                          </button>
                        ) : (
                          <button
                            data-testid={`admin-delete-${b.id}`}
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteBooking(b); }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm transition-all"
                            aria-label="Delete booking"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 bg-slate-50 border-t border-slate-200">
                        <div className="grid md:grid-cols-2 gap-4 mb-4 pt-4">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Description</p>
                            <p className="text-sm text-black">{b.description}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Expected Outcome</p>
                            <p className="text-sm text-black">{b.expected_outcome || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Property</p>
                            <p className="text-sm text-black">{b.property_address || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Urgency</p>
                            <p className="text-sm text-black capitalize">{b.urgency || 'medium'}</p>
                          </div>
                        </div>

                        {b.photos && b.photos.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Photos ({b.photos.length})</p>
                            <div className="grid grid-cols-6 gap-2">
                              {b.photos.map((p) => (
                                <div key={p} className="aspect-square bg-slate-200 rounded-sm overflow-hidden">
                                  <img src={`${API}/files/${p}?auth=${token}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {b.report_text && (
                          <div className="mb-4 p-3 bg-white border-l-2 border-blue-600 rounded-sm">
                            <p className="text-xs font-semibold text-blue-800 mb-1">CURRENT REPORT</p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{b.report_text}</p>
                          </div>
                        )}

                        <div className="mb-4 bg-white p-4 rounded-sm border border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                            <FileText className="w-3 h-3" />
                            PDF Report
                          </p>
                          {b.report_pdf_path ? (
                            <div className="flex items-center justify-between gap-3">
                              <a
                                href={`${API}/bookings/${b.id}/report-pdf?auth=${token}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-sm hover:bg-slate-800 transition-all text-sm"
                              >
                                <Download className="w-4 h-4" />
                                Download Existing
                              </a>
                              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all text-sm">
                                <Upload className="w-4 h-4" />
                                Replace PDF
                                <input
                                  type="file"
                                  accept={REPORT_ACCEPT_ATTR}
                                  onChange={(e) => handleReportPdfUpload(b.id, e.target.files[0])}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          ) : (
                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-all text-sm">
                              <Upload className="w-4 h-4" />
                              {uploadingPdf === b.id ? 'Uploading...' : 'Upload Report PDF'}
                              <input
                                type="file"
                                accept={REPORT_ACCEPT_ATTR}
                                onChange={(e) => handleReportPdfUpload(b.id, e.target.files[0])}
                                disabled={uploadingPdf === b.id}
                                className="hidden"
                              />
                            </label>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            PDF only · Max {MAX_FILE_SIZE_LABEL} · Visible to the client once stage = Report Issued
                          </p>
                          {pdfError[b.id] && (
                            <p className="text-xs text-red-700 mt-2">{pdfError[b.id]}</p>
                          )}
                        </div>

                        {/* Multi-document review (admin + client uploads, tick-to-acknowledge) */}
                        <div className="mb-4 bg-white rounded-sm border border-slate-200">
                          <BookingDocuments
                            bookingId={b.id}
                            token={token}
                            role="admin"
                            compact={false}
                          />
                        </div>

                        {b.report_pdf_path && (
                          <div
                            data-testid="admin-access-control"
                            className="mb-4 bg-white p-4 rounded-sm border border-slate-200"
                          >
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                              <Shield className="w-3 h-3" />
                              Download Access Control
                            </p>
                            <div className="grid sm:grid-cols-3 gap-3 items-end mb-2">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  data-testid="admin-lock-toggle"
                                  type="checkbox"
                                  checked={!!b.download_locked}
                                  onChange={(e) =>
                                    handleAccessUpdate(b.id, {
                                      locked: e.target.checked,
                                      expiresInDays:
                                        b.download_expires_at
                                          ? Math.max(
                                              1,
                                              Math.ceil(
                                                (new Date(b.download_expires_at).getTime() - Date.now()) /
                                                  86400000
                                              )
                                            )
                                          : null
                                    })
                                  }
                                />
                                <span>{b.download_locked ? 'Locked' : 'Unlocked'}</span>
                              </label>
                              <div className="text-xs text-slate-600">
                                <span className="block font-semibold text-slate-700 mb-1">Expires</span>
                                {b.download_expires_at
                                  ? new Date(b.download_expires_at).toLocaleDateString()
                                  : 'No expiry'}
                              </div>
                              <div className="flex gap-2">
                                {[7, 14, 30].map((days) => (
                                  <button
                                    key={days}
                                    data-testid={`admin-expire-${days}`}
                                    onClick={() =>
                                      handleAccessUpdate(b.id, {
                                        locked: b.download_locked,
                                        expiresInDays: days
                                      })
                                    }
                                    className="px-2 py-1 text-xs border border-slate-300 rounded-sm hover:bg-slate-50"
                                  >
                                    {days}d
                                  </button>
                                ))}
                                <button
                                  data-testid="admin-expire-clear"
                                  onClick={() =>
                                    handleAccessUpdate(b.id, {
                                      locked: b.download_locked,
                                      expiresInDays: null
                                    })
                                  }
                                  className="px-2 py-1 text-xs border border-slate-300 rounded-sm hover:bg-slate-50"
                                >
                                  ∞
                                </button>
                              </div>
                            </div>
                            <p className="text-[11px] text-slate-500">
                              Lock blocks downloads immediately. Expiry sets a rolling N-day window
                              from now. Admins always retain access.
                            </p>
                          </div>
                        )}

                        <div className="bg-white p-4 rounded-sm border border-slate-200">
                          <h4 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Team Actions</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">Advance to Stage</label>
                              <select
                                data-testid={ADMIN.stageSelect}
                                value={stageDrafts[b.id] || b.stage}
                                onChange={(e) => setStageDrafts({ ...stageDrafts, [b.id]: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                              >
                                {nextStageOptions.map((s) => (
                                  <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">
                                Report / Recommendation (sent on stage 'Report Issued')
                              </label>
                              <textarea
                                data-testid={ADMIN.reportTextarea}
                                value={reportDrafts[b.id] ?? b.report_text ?? ''}
                                onChange={(e) => setReportDrafts({ ...reportDrafts, [b.id]: e.target.value })}
                                rows="4"
                                placeholder="Type the initial findings and next-step recommendations..."
                                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">
                                Recommended Next Stage Service (optional)
                              </label>
                              <select
                                value={serviceDrafts[b.id] || b.next_stage_service_id || ''}
                                onChange={(e) => setServiceDrafts({ ...serviceDrafts, [b.id]: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                              >
                                <option value="">— None —</option>
                                {services.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.category} → {s.name} (A${s.deposit_amount} deposit)
                                  </option>
                                ))}
                              </select>
                            </div>

                            <button
                              data-testid={ADMIN.advanceStageButton}
                              onClick={() => handleStageUpdate(b.id)}
                              disabled={savingId === b.id}
                              className="px-5 py-2 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium disabled:opacity-50"
                            >
                              {savingId === b.id ? 'Saving...' : 'Save Update'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Admin password-reset modal */}
      {resetUserTarget && (
        <div
          data-testid="admin-reset-password-modal"
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
          onClick={() => !resetSaving && setResetUserTarget(null)}
        >
          <div className="bg-white max-w-md w-full p-6 rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                  Reset password
                </h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  {resetUserTarget.name} · {resetUserTarget.email}
                </p>
              </div>
            </div>
            <label className="block text-xs font-medium text-slate-600 mb-1">New password (min 6 characters)</label>
            <input
              data-testid="admin-reset-password-input"
              type="text"
              value={resetPasswordValue}
              onChange={(e) => setResetPasswordValue(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600 font-mono"
              placeholder="e.g. NewPass123!"
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">
              Share this password securely with the user — they can change it after signing in.
            </p>
            {resetError && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 text-xs text-red-800 rounded-sm">
                {resetError}
              </div>
            )}
            <div className="flex gap-3 justify-end mt-5">
              <button
                disabled={resetSaving}
                onClick={() => setResetUserTarget(null)}
                className="px-4 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="admin-reset-password-confirm"
                disabled={resetSaving}
                onClick={handleResetUserPassword}
                className="px-4 py-2 bg-blue-600 text-white rounded-sm text-sm font-medium hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Key className="w-4 h-4" />
                {resetSaving ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin edit-user modal */}
      {editUserTarget && (
        <div
          data-testid="admin-edit-user-modal"
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
          onClick={() => !editSaving && setEditUserTarget(null)}
        >
          <div className="bg-white max-w-md w-full p-6 rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <Edit3 className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                  Edit user profile
                </h3>
                <p className="text-sm text-slate-600 mt-0.5">{editUserTarget.role} · {editUserTarget.id.slice(0,8)}…</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Full name</label>
                <input
                  data-testid="admin-edit-user-name"
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                <input
                  data-testid="admin-edit-user-email"
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                <input
                  data-testid="admin-edit-user-phone"
                  value={editUserForm.phone}
                  onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Organization</label>
                <input
                  data-testid="admin-edit-user-org"
                  value={editUserForm.organization}
                  onChange={(e) => setEditUserForm({ ...editUserForm, organization: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>
            {editError && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 text-xs text-red-800 rounded-sm">
                {editError}
              </div>
            )}
            <div className="flex gap-3 justify-end mt-5">
              <button
                disabled={editSaving}
                onClick={() => setEditUserTarget(null)}
                className="px-4 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="admin-edit-user-save"
                disabled={editSaving}
                onClick={handleSaveUser}
                className="px-4 py-2 bg-black text-white rounded-sm text-sm font-medium hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50"
              >
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin delete-booking confirmation modal */}
      {confirmDeleteBooking && (
        <div
          data-testid="admin-delete-modal"
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
          onClick={() => !deletingId && setConfirmDeleteBooking(null)}
        >
          <div
            className="bg-white max-w-md w-full p-6 rounded-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                  Delete booking (admin)?
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  <strong className="text-black">{confirmDeleteBooking.service_type}</strong> ·{' '}
                  A${(confirmDeleteBooking.budget || 0).toFixed(2)} ·{' '}
                  {confirmDeleteBooking.payment_status}
                </p>
              </div>
            </div>
            <p className="text-sm text-slate-700 bg-amber-50 border-l-2 border-amber-500 p-3 rounded-sm mb-4">
              The booking will be hidden from the client. It remains visible to admins under
              the <strong>Deleted</strong> filter and can be restored from there.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                data-testid="admin-delete-cancel"
                disabled={!!deletingId}
                onClick={() => setConfirmDeleteBooking(null)}
                className="px-4 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="admin-delete-confirm"
                disabled={!!deletingId}
                onClick={handleDeleteBooking}
                className="px-4 py-2 bg-red-600 text-white rounded-sm text-sm font-medium hover:bg-red-700 transition-all inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {deletingId ? 'Deleting…' : 'Delete booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
